import { supabase } from './supabase';

export interface CheckoutSession {
  session_id: string;
  url: string;
  status: string;
}

export interface TokenPackage {
  id: string;
  name: string;
  tokens: number;
  price_cents: number;
  metadata?: {
    is_company_purchase?: boolean;
    company_name?: string | null;
  };
}

export const stripeCheckoutService = {
  // Create a Stripe Checkout session
  async createCheckoutSession(
    tokenPackage: TokenPackage,
    userId: string
  ): Promise<CheckoutSession> {
    try {
      console.log('🔧 Creating checkout session for package:', tokenPackage.name);
      
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('Supabase URL not configured');
      }

      // Create success and cancel URLs
      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/payment-success?session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${baseUrl}/buy-tokens`;

      const response = await fetch(`${supabaseUrl}/functions/v1/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          tokenPackageId: tokenPackage.id,
          userId: userId,
          successUrl: successUrl,
          cancelUrl: cancelUrl,
          metadata: tokenPackage.metadata
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create checkout session');
      }

      const data = await response.json();
      console.log('✅ Checkout session created:', data.session_id);
      
      return {
        session_id: data.session_id,
        url: data.url,
        status: data.status
      };
    } catch (error) {
      console.error('❌ Error creating checkout session:', error);
      throw error;
    }
  },

  // Redirect user to Stripe Checkout
  async redirectToCheckout(tokenPackage: TokenPackage, userId: string): Promise<void> {
    try {
      const session = await this.createCheckoutSession(tokenPackage, userId);
      
      // Redirect to Stripe Checkout
      window.location.href = session.url;
    } catch (error) {
      console.error('❌ Error redirecting to checkout:', error);
      throw error;
    }
  },

  // Get checkout session details
  async getCheckoutSession(sessionId: string) {
    try {
      const { data, error } = await supabase
        .rpc('get_checkout_session', { p_session_id: sessionId });

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      console.error('❌ Error getting checkout session:', error);
      throw error;
    }
  },

  // Check if a checkout session was successful
  async checkPaymentStatus(sessionId: string): Promise<{
    success: boolean;
    session?: any;
    error?: string;
  }> {
    try {
      const session = await this.getCheckoutSession(sessionId);
      
      if (!session || session.length === 0) {
        return {
          success: false,
          error: 'Session not found'
        };
      }

      const sessionData = session[0];
      
      if (sessionData.status === 'complete') {
        return {
          success: true,
          session: sessionData
        };
      } else {
        return {
          success: false,
          error: `Payment status: ${sessionData.status}`
        };
      }
    } catch (error) {
      console.error('❌ Error checking payment status:', error);
      return {
        success: false,
        error: 'Failed to check payment status'
      };
    }
  }
}; 